// Side-effect import must come first to register Chart.js time scale adapter
// eslint-disable-next-line import/order
import 'chartjs-adapter-date-fns'

import {useEffect, useMemo, useRef} from 'react'
import {useIntl} from 'react-intl'
import {useSelector} from 'react-redux'
import styled from '@emotion/styled'
import {Chart as ChartJS, ChartConfiguration, ChartOptions} from 'chart.js/auto'
import {getIsRtlLanguage} from 'modules/meta/selectors'
import {getShowTotalPrices} from 'modules/sapiSearch/selectors'

import {Box} from '@vio/atlas/helpers/Box'
import {SkeletonLoader} from '@vio/atlas/SkeletonLoader'
import {cssTheme} from '@vio/atlas/src/themes'
import {getCssVariableValue} from '@vio/atlas/src/utils/getCssVariableValue'
import {Text} from '@vio/atlas/Text'
import {linariaMq} from '@vio/atlas/utils/breakpoints'
import {FormattedMessageWrapper} from '@vio/core/src/localization/components/FormattedMessage'
import {formatAbbreviatedPriceValue} from '@vio/core/src/price/business/formatPrice'
import {logger} from '@vio/core/src/utils/logging/logger'

import {
  addNowPointToSimilarHotels,
  buildChartDatasets,
  buildLabelsAndHotelPrices,
  computeYAxisConfig
} from '../../helpers/chartDataHelpers'
import {
  backgroundColorPlugin,
  createCustomDotsPlugin,
  createCustomXAxisLabelsPlugin,
  createStaticLabelsPlugin,
  createVerticalLinePlugin
} from '../../helpers/priceHistoryChartPlugins'
import {CHART_COLORS, CHART_LAYOUT} from '../../utils/constants'
import {priceTrendMessages} from '../../utils/messages'

// Re-export types for backward compatibility
import type {PriceTrendsHistoryResponse} from '../../services/priceInsightsApi'

const AccordionWrapper = styled(Box)`
  border: 1px solid ${cssTheme.colors.border.neutral.secondary};
  padding: ${cssTheme.layout.spacing.s400};
  ${linariaMq.desktopXs} {
    padding: ${cssTheme.layout.spacing.s500};
  }
  border-radius: ${cssTheme.layout.radius.lg};
`

const ChartWrapper = styled.div`
  position: relative;
  height: 236px;
  width: 100%;
  padding-right: ${cssTheme.layout.spacing.s100};
`

export const PriceHistoryChart = ({
  data,
  offerPrice,
  isLoading
}: {
  data: PriceTrendsHistoryResponse | undefined
  offerPrice?: number
  isLoading?: boolean
}) => {
  const chartRef = useRef<HTMLCanvasElement>(null)
  const chartInstanceRef = useRef<ChartJS<'line'> | null>(null)
  const {formatMessage} = useIntl()
  const showTotalPrices = useSelector(getShowTotalPrices)
  const isRtl = useSelector(getIsRtlLanguage)

  // Extract chart-relevant data (safe to access even if undefined)
  const currency = data?.currency ?? ''
  const hasHotelHistory = (data?.hotelHistory?.length ?? 0) > 0
  const isChartVisible = data && hasHotelHistory

  const {locale} = useIntl()

  // Create a price formatter using abbreviated price values
  const formatPrice = useMemo(
    () => (value: number) =>
      formatAbbreviatedPriceValue({
        locale,
        value,
        currency,
        useShortCurrency: false,
        roundingStrategy: 'round'
      }),
    [locale, currency]
  )

  useEffect(() => {
    // Only render chart if we have data, chart ref, and hotel history
    const hotelHistory = data?.hotelHistory ?? []
    if (!data || !chartRef.current || !hotelHistory.length) return

    // Destroy previous chart instance
    if (chartInstanceRef.current) {
      chartInstanceRef.current.destroy()
    }

    // Build labels/prices and add "now" point with offerPrice when provided
    // Use nightlyPrice by default, or price (total) when showTotalPrices is true
    const {labels, hotelPrices} = buildLabelsAndHotelPrices(
      hotelHistory,
      offerPrice,
      showTotalPrices
    )

    // Compute X-axis span from actual data, rounded up to nearest week
    const xAxisTotalDays = CHART_LAYOUT.X_AXIS_TOTAL_DAYS

    // Extract similar hotels prices
    const similarHotelsPrices = data?.similarHotelsHistory?.map(point =>
      showTotalPrices ? point.price : point.nightlyPrice
    )

    // Add "now" point to similar hotels to match hotel history (avoids visual gap)
    const similarPrices = addNowPointToSimilarHotels(
      similarHotelsPrices,
      offerPrice !== undefined
    )

    const {stepSize, yAxisMin, yAxisMax, desiredTicks, ticksAbove, ticksBelow} =
      computeYAxisConfig(hotelPrices, similarPrices)

    // Create custom x-axis labels plugin for this render
    const customXAxisLabelsPlugin = createCustomXAxisLabelsPlugin(
      labels,
      formatMessage,
      xAxisTotalDays
    )

    // Create vertical line plugin for this render
    const verticalLinePlugin = createVerticalLinePlugin(labels)

    // Resolve brand primary color from the current theme at render time.
    // CSS variables can't be used directly in canvas — getCssVariableValue reads
    // the computed hex value so the chart line color matches the active brand theme.
    const brandPrimary =
      getCssVariableValue(cssTheme.colors.content.brand.primary).trim() ||
      CHART_COLORS.primary

    // Build datasets using helper function
    const datasets = buildChartDatasets(
      hotelPrices,
      similarPrices,
      formatMessage,
      brandPrimary
    )

    const options: ChartOptions<'line'> = {
      responsive: true,
      maintainAspectRatio: false,
      animation: false,
      plugins: {
        legend: {
          display: true,
          position: 'bottom',
          align: 'center',
          rtl: isRtl, // Places the color indicator on the right of the text in RTL
          onClick: null, // Disable legend click interactions
          onHover: null, // Disable legend hover interactions
          title: {
            display: true,
            padding: {top: -8}
          },
          labels: {
            usePointStyle: true,
            pointStyle: 'line',
            borderRadius: 2,
            padding: 25,
            font: {
              ...cssTheme.typography.bodyXS
            },
            color: CHART_COLORS.subtitleText
          }
        },
        tooltip: {
          enabled: false
        }
      },
      scales: {
        x: {
          type: 'time',
          // Span from the newest point back by the computed total days
          min:
            new Date(labels[0]).getTime() -
            xAxisTotalDays * 24 * 60 * 60 * 1000,
          time: {
            unit: 'day',
            displayFormats: {
              day: 'MMM d'
            },
            tooltipFormat: 'MMM d, yyyy'
          },
          grid: {
            display: false
          },
          border: {
            display: false
          },
          ticks: {
            display: false, // Hide default ticks, we'll draw custom ones
            source: 'auto',
            maxTicksLimit: 5
          },
          afterFit(scale) {
            scale.height = CHART_LAYOUT.X_AXIS_TOTAL_HEIGHT
          }
        },
        y: {
          min: yAxisMin,
          max: yAxisMax,
          grid: {
            drawTicks: false,
            // Hide first and last grid lines (top and bottom) by making them transparent
            color: context => {
              const {
                tick,
                chart: {scales}
              } = context
              const {value} = tick
              const {y: scale} = scales
              // Return transparent for min and max values
              if (value === scale.min || value === scale.max) {
                return 'transparent'
              }
              return CHART_COLORS.gridLine
            }
          },
          border: {
            display: false
          },
          ticks: {
            callback: function (value) {
              return formatPrice(Number(value))
            },
            color: CHART_COLORS.text,
            font: {
              ...cssTheme.typography.bodyXS
            },
            padding: 8,
            stepSize,
            autoSkip: false,
            maxTicksLimit: desiredTicks + ticksBelow + ticksAbove
          }
        }
      },
      interaction: {
        intersect: false,
        mode: 'index'
      },
      hover: {
        mode: null
      }
    }

    // Create custom dots plugin for this render
    const customDotsPlugin = createCustomDotsPlugin(brandPrimary)
    const staticLabelsPlugin = createStaticLabelsPlugin(
      formatPrice,
      brandPrimary
    )

    const config: ChartConfiguration<'line'> = {
      type: 'line',
      data: {
        labels,
        datasets
      },
      options,
      plugins: [
        backgroundColorPlugin,
        verticalLinePlugin,
        customDotsPlugin,
        customXAxisLabelsPlugin,
        staticLabelsPlugin
      ]
    }

    // Create new chart instance
    try {
      chartInstanceRef.current = new ChartJS(chartRef.current, config)
    } catch (error) {
      logger.error('Failed to create price history chart:', error)
      // Chart will simply not render, bottom sheet still functions
    }

    // Cleanup function
    return () => {
      if (chartInstanceRef.current) {
        chartInstanceRef.current.destroy()
      }
    }
    // Only depend on chart-relevant data, not entire data object
  }, [data, formatPrice, formatMessage, offerPrice, showTotalPrices, isRtl])

  // Don't render anything if not loading and no valid data to display
  if (!isLoading && !isChartVisible) {
    return null
  }

  return (
    <AccordionWrapper>
      <Box marginBottom="s400" marginTop="s200">
        <Text variant="titleS" colorPath="content.neutral.primary">
          <FormattedMessageWrapper {...priceTrendMessages.priceHistory} />
        </Text>
      </Box>
      {isLoading ? (
        <Box data-id="price-history-skeleton" paddingBottom="s400">
          <SkeletonLoader width={350} />
        </Box>
      ) : isChartVisible ? (
        <Box>
          <ChartWrapper>
            <canvas ref={chartRef} />
          </ChartWrapper>
        </Box>
      ) : null}
    </AccordionWrapper>
  )
}
